import React, { useState, useRef } from 'react';
import TopBarHeader from './TopBarHeader';
import '../style/documentosSubidos.css';
import '../style/App.css';
import icon from '../assets/icon-upload.png'; // Import the upload icon
import eyeIcon from '../assets/eye-icon.svg'; // Import the eye icon
import trashIcon from '../assets/trash-icon.svg'; // Import the trash icon

// Función para normalizar texto eliminando tildes y caracteres especiales
const normalizeText = (text) => {
  return text
    .normalize('NFD')  // Normaliza el texto descomponiendo los caracteres acentuados
    .replace(/[\u0300-\u036f]/g, '')  // Elimina los diacríticos (tildes, diéresis, etc.)
    .replace(/[^\w\s.-]/g, ''); // Elimina caracteres especiales excepto letras, números, guiones, puntos y espacios
};

// Función específica para normalizar nombres de archivos (reemplaza espacios con guiones bajos)
const normalizeFileName = (fileName) => {
  return normalizeText(fileName)
    .replace(/\s+/g, '_'); // Reemplaza uno o más espacios con guión bajo
};

// Opciones para el tipo de documento
const TIPOS_DOCUMENTO = [
  "Fotocopia Carnet de Identidad",
  "Certificado Isapre/Fonasa",
  "Certificado AFP",
  "Documentos Extranjeros",
  "Curriculum Vitae",
  "Otros documentos"
];

const DocumentosTrabajadores = () => {
  const [rut, setRut] = useState('');
  const [documentos, setDocumentos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadResults, setUploadResults] = useState([]);
  const [rutError, setRutError] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success' // 'success', 'error', 'warning'
  });
  // Nueva lista para mantener todos los RUTs detectados
  const [rutsDetectados, setRutsDetectados] = useState([]);
  const inputRef = useRef(null);
  const [rutInvalido, setRutInvalido] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [confirmaciónConsciente, setConfirmaciónConsciente] = useState(false);

  // Function to validate Chilean RUT
  const validateRut = (rut) => {
    if (!rut) return false;
    
    // Remove dots and dash
    const cleanRut = rut.replace(/\./g, '').replace(/-/g, '');
    
    // Check if it has the correct format
    if (!/^\d{7,8}[0-9K]$/i.test(cleanRut)) return false;
    
    // Separate body and verification digit
    const body = cleanRut.slice(0, -1);
    const dv = cleanRut.slice(-1).toUpperCase();
    
    // Calculate verification digit
    let sum = 0;
    let multiplier = 2;
    
    // Iterate from right to left
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body.charAt(i)) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    
    const expectedDV = 11 - (sum % 11);
    let calculatedDV;
    
    if (expectedDV === 11) calculatedDV = '0';
    else if (expectedDV === 10) calculatedDV = 'K';
    else calculatedDV = expectedDV.toString();
    
    return calculatedDV === dv;
  };

  // Función para encontrar el RUT más frecuente en la lista
  const encontrarRutMasFrecuente = (listaRuts) => {
    if (!listaRuts || listaRuts.length === 0) return null;
    
    // Crear contador de frecuencias
    const contadorRuts = {};
    
    // Contar frecuencia de cada RUT
    listaRuts.forEach(rutItem => {
      // Ignorar explícitamente "sin-rut" y validar el resto
      if (rutItem && rutItem !== 'sin-rut' && validateRut(rutItem)) {
        contadorRuts[rutItem] = (contadorRuts[rutItem] || 0) + 1;
      }
    });
    
    console.log('Contador de RUTs:', contadorRuts);
    
    // Si no hay RUTs válidos, retornar null
    if (Object.keys(contadorRuts).length === 0) return null;
    
    // Encontrar el RUT con mayor frecuencia
    let maxFrecuencia = 0;
    let rutsMasFrecuentes = [];
    
    for (const [rutActual, frecuencia] of Object.entries(contadorRuts)) {
      if (frecuencia > maxFrecuencia) {
        maxFrecuencia = frecuencia;
        rutsMasFrecuentes = [rutActual];
      } else if (frecuencia === maxFrecuencia) {
        rutsMasFrecuentes.push(rutActual);
      }
    }
    
    // Si hay empate, elegir cualquiera de los RUTs más frecuentes
    const rutSeleccionado = rutsMasFrecuentes[0];
    console.log(`RUT más frecuente: ${rutSeleccionado} (aparece ${maxFrecuencia} veces). Candidatos en caso de empate:`, rutsMasFrecuentes);
    
    return rutSeleccionado;
  };

  const handleRutChange = (e) => {
    // Formatear RUT automáticamente
    let value = e.target.value.replace(/\./g, '').replace(/-/g, '');
    
    if (value.length > 0) {
      // Permitir números y la letra K (mayúscula o minúscula)
      value = value.replace(/[^\dkK]/g, '');
      
      // Convertir 'k' minúscula a 'K' mayúscula si existe
      value = value.replace(/k/g, 'K');
      
      // Aplicar formato correcto XX.XXX.XXX-X
      if (value.length > 1) {
        let formatted = '';
        let body = value.slice(0, -1);
        let dv = value.slice(-1);
        
        // Invertir para formatear desde el final
        body = body.split('').reverse().join('');
        
        // Agregar puntos cada 3 dígitos
        for (let i = 0; i < body.length; i++) {
          if (i > 0 && i % 3 === 0) {
            formatted = '.' + formatted;
          }
          formatted = body[i] + formatted;
        }
        
        // Agregar dígito verificador
        value = formatted + '-' + dv;
      }
    }
    
    setRut(value);
    
    // Validate RUT when it changes
    if (value) {
      setRutError(!validateRut(value));
    } else {
      setRutError(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      
      // Validar archivos antes de procesarlos
      files.forEach(file => {
        // Validar tipo de archivo
        const typeValidation = validateFileType(file);
        if (!typeValidation.valid) {
          showNotification(typeValidation.error, "error");
          return;
        }
        
        // Validar tamaño de archivo
        const sizeValidation = validateFileSize(file);
        if (!sizeValidation.valid) {
          showNotification(sizeValidation.error, "error");
          return;
        }
        
        // Procesar cada archivo individualmente
        updateDocumentos(file);
      });
    }
  };
  
  const handleButtonClick = () => {
    inputRef.current.click();
  };
  
  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      
      // Verificar que los archivos sean de tipos permitidos y tamaño adecuado
      files.forEach(file => {
        // Validar tipo de archivo
        const typeValidation = validateFileType(file);
        if (!typeValidation.valid) {
          showNotification(typeValidation.error, "error");
          return;
        }
        
        // Validar tamaño de archivo
        const sizeValidation = validateFileSize(file);
        if (!sizeValidation.valid) {
          showNotification(sizeValidation.error, "error");
          return;
        }
        
        // Si pasa todas las validaciones, procesar el archivo
        updateDocumentos(file);
      });
    }
  };
  
  const updateDocumentos = async (file) => {
    // Normalizar nombre del archivo para eliminar tildes y caracteres especiales
    const normalizedFileName = normalizeFileName(file.name);
    
    // Ya no normalizamos el tipo de documento
    
    // Crear nuevo documento con estado de carga
    const newDoc = {
      id: Date.now() + Math.random(), // ID único para cada documento
      tipo: TIPOS_DOCUMENTO[0], // Por defecto, el primer tipo
      // Eliminamos el campo tipoNormalizado
      nombre: file.name, // Mantener nombre original para mostrar en la UI
      nombreNormalizado: normalizedFileName, // Nombre normalizado para enviar
      archivo: file,
      status: 'uploading', // Indicar que está cargando
      gsutilUri: null // Inicializar el campo gsutilUri
    };
    
    // Función para buscar el URI de gsutil en las respuestas
    const findGsutilUri = (obj) => {
      // Nombres comunes para el URI de gsutil
      const possibleNames = [
        'gsutilUri', 'gsutil_uri', 'uri', 'gcsUri', 'gs_uri', 'gcsPath', 'gs_path', 'path',
        'gcs_path', 'url', 'location', 'storage_uri', 'storageUri', 'fileUri', 'file_uri',
        'bucket_path', 'bucketPath', 'source', 'destination', 'filePath', 'file_path',
        'storagePath', 'storage_path'
      ];
      
      // Si el objeto es null o undefined, retornar null
      if (!obj) return null;
      
      // Si el objeto es una string que comienza con gs://, retornar directamente
      if (typeof obj === 'string' && obj.startsWith('gs://')) {
        return obj;
      }
      
      // Primero buscar directamente en el objeto
      for (const name of possibleNames) {
        if (obj[name] && typeof obj[name] === 'string') {
          // Si contiene gs:// en cualquier parte, es muy probable que sea el URI
          if (obj[name].includes('gs://')) {
            console.log(`Encontrado URI de gsutil en propiedad ${name}:`, obj[name]);
            return obj[name];
          }
        }
      }
      
      // Si no se encuentra, buscar en cada propiedad que sea un objeto
      for (const key in obj) {
        if (obj[key] && typeof obj[key] === 'object') {
          // Si es un array, revisar cada elemento
          if (Array.isArray(obj[key])) {
            for (const item of obj[key]) {
              const nestedUri = findGsutilUri(item);
              if (nestedUri) return nestedUri;
            }
          } else {
            // Si es un objeto, buscar recursivamente
            const nestedUri = findGsutilUri(obj[key]);
            if (nestedUri) return nestedUri;
          }
        }
      }
      
      // Si sigue sin encontrarse, buscar en propiedades que sean string y contengan "gs://"
      for (const key in obj) {
        if (typeof obj[key] === 'string' && obj[key].includes('gs://')) {
          console.log(`Encontrado URI de gsutil en propiedad ${key}:`, obj[key]);
          return obj[key];
        }
      }
      
      return null;
    };
    
    // Actualizar lista de documentos
    setDocumentos(prevDocs => [...prevDocs, newDoc]);
    
    // Crear un FormData para enviar el archivo
    const formData = new FormData();
    formData.append('rut', rut || 'sin-rut'); // Si no hay RUT, enviamos un valor por defecto
    
    // Crear un nuevo File con el nombre normalizado para enviar al servidor
    const fileToUpload = new File([file], normalizedFileName, { type: file.type });
    formData.append('files', fileToUpload, normalizedFileName);
    formData.append('tipos', TIPOS_DOCUMENTO[0]); // Tipo por defecto sin normalizar
    
    try {
      // URL de tu Cloud Function
      //const cloudFunctionUrl = `${process.env.REACT_APP_BASE_URL}`;
      const cloudFunctionUrl = "https://labs-gcs-upload-main-126625280314.us-central1.run.app/"
      // Enviar el archivo a la Cloud Function
      const response = await fetch(cloudFunctionUrl, {
        method: 'POST',
        body: formData,
      });
      
      const result = await response.json();
      
      if (response.ok) {
        console.log('Respuesta del servidor:', result);
        
        // Buscar URI de gsutil en toda la respuesta como string
        const responseStr = JSON.stringify(result);
        const gsMatches = responseStr.match(/gs:\/\/[a-zA-Z0-9._/-]+/g);
        if (gsMatches && gsMatches.length > 0) {
          console.log('URIs de gsutil encontrados en la respuesta:', gsMatches);
        }
        
        // Verificar si la respuesta tiene la estructura esperada
        if (result.files && result.files.length > 0) {
          const fileInfo = result.files[0]; // Tomamos el primer archivo (solo enviamos uno)
          
          console.log('Información del archivo:', fileInfo);
          
          // Buscar cualquier URI de gsutil en el objeto fileInfo
          let gsutilUri = null;
          
          // Método 1: Buscar coincidencias de patrón en el objeto fileInfo convertido a string
          const fileInfoStr = JSON.stringify(fileInfo);
          const matches = fileInfoStr.match(/gs:\/\/[a-zA-Z0-9._/-]+/g);
          
          if (matches && matches.length > 0) {
            console.log('URIs de gsutil encontrados en fileInfo:', matches);
            gsutilUri = matches[0]; // Usar el primer match
          }
          
          // Método 2: Si no se encontró en fileInfo, buscar en toda la respuesta
          if (!gsutilUri && gsMatches && gsMatches.length > 0) {
            console.log('Usando URI encontrado en la respuesta completa');
            gsutilUri = gsMatches[0]; // Usar el primer match
          }
          
          // Método 3: Buscar en campos específicos conocidos
          if (!gsutilUri) {
            const possibleFields = [
              'gsutilUri', 'gsutil_uri', 'gcsUri', 'gcs_uri', 'uri', 'path', 
              'storage', 'fileUri', 'file_uri', 'url', 'location', 'gcsPath'
            ];
            
            for (const field of possibleFields) {
              if (fileInfo[field] && typeof fileInfo[field] === 'string' && 
                  fileInfo[field].includes('gs://')) {
                gsutilUri = fileInfo[field];
                console.log(`URI encontrado en campo '${field}':`, gsutilUri);
                break;
              }
            }
          }
          
          // Also look for file reference or name if no gs URI is found
          const getFileReference = () => {
            if (fileInfo.name) return `Nombre en servidor: ${fileInfo.name}`;
            if (fileInfo.filename) return `Archivo: ${fileInfo.filename}`;
            if (fileInfo.file_name) return `Archivo: ${fileInfo.file_name}`;
            if (fileInfo.id) return `ID: ${fileInfo.id}`;
            return null;
          };
          
          const fileReference = getFileReference();
          
          // Obtener el tipo del documento detectado o usar el tipo por defecto
          const detectedType = fileInfo.tipoDocumento || newDoc.tipo;
          
          console.log('URI de gsutil final:', gsutilUri);
          console.log('Referencia de archivo:', fileReference);
          console.log('Tipo de documento detectado:', detectedType);
          
          // Actualizar el documento con la información recibida
          setDocumentos(prevDocs => 
            prevDocs.map(doc => 
              doc.id === newDoc.id ? { 
                ...doc, 
                status: 'success',
                tipo: detectedType,
                analysis: fileInfo,
                gsutilUri: gsutilUri,
                fileReference: fileReference
              } : doc
            )
          );
          
          // Log de documentos para depuración
          setTimeout(() => {
            console.log('Documentos actualizados:', documentos);
          }, 100);
          
          // Si se extrajo un RUT, agregarlo a la lista de RUTs detectados y actualizar el input
          if (fileInfo.RUT) {
            // Log para depuración - mostrar explícitamente si es "sin-rut"
            console.log(`RUT detectado en documento: "${fileInfo.RUT}" ${fileInfo.RUT === 'sin-rut' ? '(SIN RUT)' : ''}`);
            
            // Agregar el RUT a la lista de RUTs detectados
            setRutsDetectados(prevRuts => {
              // Crear nueva lista con el RUT actual
              const nuevaLista = [...prevRuts, fileInfo.RUT];
              console.log("Lista actualizada de RUTs detectados:", nuevaLista);
              
              // Encontrar el RUT más frecuente
              const rutMasFrecuente = encontrarRutMasFrecuente(nuevaLista);
              
              // Si hay un RUT más frecuente, actualizar el input
              if (rutMasFrecuente) {
                console.log(`RUT más frecuente seleccionado: ${rutMasFrecuente}`);
                setRut(rutMasFrecuente);
                setRutError(!validateRut(rutMasFrecuente));
              } else {
                console.log("No se encontró un RUT válido entre los detectados");
                // Si no hay RUT válido, limpiamos el campo
                setRut('');
                setRutError(false);
              }
              
              return nuevaLista;
            });
          }
        } else {
          // Si no hay resultados de análisis, marcar como éxito pero sin cambios
          setDocumentos(prevDocs => 
            prevDocs.map(doc => 
              doc.id === newDoc.id ? { ...doc, status: 'success' } : doc
            )
          );
        }
      } else {
        // Actualizar el estado del documento a 'error'
        setDocumentos(prevDocs => 
          prevDocs.map(doc => 
            doc.id === newDoc.id ? { 
              ...doc, 
              status: 'error', 
              error: result.error || 'Error desconocido' 
            } : doc
          )
        );
        
        console.error('Error al enviar documento:', result.error || 'Error desconocido');
      }
    } catch (error) {
      console.error('Error al enviar documento:', error);
      
      // Actualizar el estado del documento a 'error'
      setDocumentos(prevDocs => 
        prevDocs.map(doc => 
          doc.id === newDoc.id ? { 
            ...doc, 
            status: 'error', 
            error: error.message 
          } : doc
        )
      );
    }
  };

  // Función para manejar el cambio de tipo de documento
  const handleDocumentTypeChange = (docId, newType) => {
    setDocumentos(prevDocs => 
      prevDocs.map(doc => 
        doc.id === docId ? { 
          ...doc, 
          tipo: newType
        } : doc
      )
    );
  };

  // Función para eliminar un documento de la lista
  const handleDeleteDocument = (docId) => {
    setDocumentos(prevDocs => prevDocs.filter(doc => doc.id !== docId));
  };

  // Función para limpiar todos los documentos
  const handleClearAll = () => {
    if (documentos.length > 0) {
      setDocumentos([]);
      setConfirmaciónConsciente(false);
      // Ahora también limpiamos la lista de RUTs detectados
      setRutsDetectados([]);
      // También limpiamos el RUT del input
      setRut('');
      setRutError(false);
      showNotification("Se han eliminado todos los documentos", "success");
    }
  };

  // Función para abrir el modal y mostrar el documento
  const openModal = (documento) => {
    const fileURL = URL.createObjectURL(documento.archivo);
    const fileType = getFileTypeInfo(documento.archivo).type;
    setSelectedDocument({ 
      archivoURL: fileURL,
      fileType: fileType,
      fileName: documento.nombre
    });
    setIsModalOpen(true);
  };

  // Función para cerrar el modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDocument(null);
  };

  // Función para enviar documentos a la Cloud Function de Bizneo
  const handleConfirmar = async () => {
    // Verificar que hay documentos subidos
    if (documentos.length === 0) {
      showNotification("No hay documentos para confirmar", "error");
      return;
    }
    
    // Verificar que hay un RUT válido
    if (!rut || rutError) {
      showNotification("Por favor ingrese un RUT válido antes de confirmar", "error");
      return;
    }
    
    // Verificar si hay categorías duplicadas (excluyendo "Otros documentos" y "Documentos Extranjeros")
    const tiposDocumentos = documentos.map(doc => doc.tipo);
    
    // Filtrar tipos que no son "Otros documentos" ni "Documentos Extranjeros"
    const tiposParaVerificar = tiposDocumentos.filter(
      tipo => tipo !== "Otros documentos" && tipo !== "Documentos Extranjeros"
    );
    
    // Contar documentos de tipo "Fotocopia Carnet de Identidad"
    const carnetDocumentos = documentos.filter(doc => doc.tipo === "Fotocopia Carnet de Identidad");
    
    // Validar que no haya más de 2 documentos de Carnet de Identidad
    if (carnetDocumentos.length > 2) {
      showNotification("Error: No se permiten más de 2 documentos de Carnet de Identidad", "error");
      return;
    }
    
    // Verificar duplicados solo en los tipos restringidos, pero permitir exactamente 2 Carnet de Identidad
    const tiposDuplicados = [];
    const contadorTipos = {};
    
    // Contar ocurrencias de cada tipo
    tiposParaVerificar.forEach(tipo => {
      contadorTipos[tipo] = (contadorTipos[tipo] || 0) + 1;
    });
    
    // Encontrar duplicados, exceptuando el caso de exactamente 2 Carnets de Identidad
    for (const [tipo, cantidad] of Object.entries(contadorTipos)) {
      if (cantidad > 1 && !(tipo === "Fotocopia Carnet de Identidad" && cantidad === 2)) {
        tiposDuplicados.push(tipo);
      }
    }
    
    if (tiposDuplicados.length > 0) {
      const tiposDuplicadosUnicos = [...new Set(tiposDuplicados)];
      showNotification(`Error: Se están subiendo categorías duplicadas: ${tiposDuplicadosUnicos.join(", ")}. Por favor elimine o cambie el tipo de documento duplicado.`, "error");
      return;
    }
    
    // Mostrar estado actual para depuración
    console.log("=== INICIO CONFIRMACIÓN ===");
    console.log("Estado de documentos:", documentos);
    console.log("RUT ingresado:", rut);
    console.log("Documentos de Carnet de Identidad:", carnetDocumentos.length);
    
    // Mostrar indicador de carga
    setUploading(true);
    
    try {
      // URL de la Cloud Function para Bizneo
      //const updateBizneoUrl = `${process.env.updateBizneoUrl}`;
      const updateBizneoUrl = "https://update-bizneo-main-126625280314.us-central1.run.app/";
      
      // URL alternativa para cuando hay exactamente 2 carnets de identidad
      const carnetDobleFunctionUrl = "https://carga-trabajadores-carnet-main-126625280314.us-central1.run.app/";

      // Imprimir todos los documentos para verificar
      console.log("Todos los documentos:", documentos);
      
      // Obtener los documentos con gsutilUri
      const docsWithUri = documentos.filter(doc => doc.status === 'success' && doc.gsutilUri);
      const docsWithoutUri = documentos.filter(doc => !doc.gsutilUri || doc.status !== 'success');
      
      // Mostrar explícitamente cuáles tienen URI y cuáles no
      documentos.forEach(doc => {
        console.log(`Documento: ${doc.nombre} (Normalizado: ${doc.nombreNormalizado || doc.nombre})`);
        console.log(`  - Status: ${doc.status}`);
        console.log(`  - gsutilUri: ${doc.gsutilUri || 'NO TIENE'}`);
      });
      
      if (docsWithUri.length === 0) {
        console.error("No se encontraron documentos con URI de gsutil válido");
        showNotification("No hay documentos con URI de gsutil para enviar. Revisa la consola para más detalles.", "error");
        setUploading(false);
        return;
      }
      
      // Notificar sobre documentos que no se pudieron procesar correctamente
      if (docsWithoutUri.length > 0) {
        const docNombres = docsWithoutUri.map(doc => `${doc.nombre} (${doc.status === 'error' ? doc.error || 'Error desconocido' : 'Falta URI'})`);
        showNotification(`Advertencia: ${docsWithoutUri.length} documento(s) no será(n) enviado(s): ${docNombres.join(", ")}`, "warning");
      }
      
      console.log("Documentos filtrados con URI:", docsWithUri);
      
      // Preparar el RUT en el formato requerido "12345678-9" una sola vez para todos los documentos
      let formattedRut = rut.replace(/\./g, ''); // Primero quitar solo los puntos
      
      // Si no tiene el guion, asegurarnos de agregarlo correctamente
      if (!formattedRut.includes('-')) {
        // Si no tiene guion, necesitamos agregarlo antes del último dígito
        const rutSinGuion = formattedRut.replace(/-/g, ''); // Quitar cualquier guion por si acaso
        formattedRut = rutSinGuion.slice(0, -1) + '-' + rutSinGuion.slice(-1);
      }
      
      console.log("RUT formateado para enviar (será el mismo para todos los documentos):", formattedRut);
      
      // Verificar si tenemos exactamente 2 documentos de carnet de identidad
      const carnetDocumentos = docsWithUri.filter(doc => doc.tipo === "Fotocopia Carnet de Identidad");
      if (carnetDocumentos.length === 2) {
        console.log("Se detectaron exactamente 2 documentos de Carnet de Identidad, usando endpoint especial");
        
        // URL para el caso especial de 2 carnets
        const carnetDobleFunctionUrl = "https://carga-trabajadores-carnet-main-126625280314.us-central1.run.app/";
        
        try {
          // Extraer los URIs de los dos documentos de carnet
          const url1 = carnetDocumentos[0].gsutilUri;
          const url2 = carnetDocumentos[1].gsutilUri;
          
          // Preparar datos con el formato requerido
          const requestData = {
            url1: url1,
            url2: url2,
            rut: formattedRut
          };
          
          console.log("Enviando solicitud específica para 2 carnets:", requestData);
          console.log("URL del endpoint:", carnetDobleFunctionUrl);
          
          const response = await fetch(carnetDobleFunctionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData)
          });
          
          console.log("Respuesta recibida para solicitud de 2 carnets:");
          console.log(`  - Status: ${response.status}`);
          console.log(`  - OK: ${response.ok}`);
          
          const responseText = await response.text();
          console.log(`  - Texto completo: "${responseText}"`);
          
          // Crear un resultado para los carnets
          let carnetResult;
          
          if (response.ok) {
            let data;
            try {
              data = JSON.parse(responseText);
              console.log(`  - Datos JSON:`, data);
            } catch (e) {
              console.log(`  - No es JSON válido, usando como texto`);
              data = { message: responseText };
            }
            
            carnetResult = {
              success: true,
              documentType: "Fotocopia Carnet de Identidad (2 documentos)",
              count: 2,
              data: data
            };
            
            console.log("Los documentos de Carnet de Identidad fueron enviados correctamente");
          } else {
            // Si hay error al procesar los 2 carnets
            let errorMsg;
            try {
              const errorJson = JSON.parse(responseText);
              errorMsg = errorJson.error || errorJson.message || `Error ${response.status}`;
            } catch (e) {
              errorMsg = `Error ${response.status}: ${response.statusText || responseText}`;
            }
            
            carnetResult = {
              success: false,
              documentType: "Fotocopia Carnet de Identidad (2 documentos)",
              count: 2,
              error: errorMsg
            };
            
            console.log(`Error al enviar los documentos de Carnet de Identidad: ${errorMsg}`);
          }
          
          // Guardar el resultado para mostrar al final
          const results = [carnetResult];
          
          // Continuar con el procesamiento del resto de documentos
          console.log("Continuando con el procesamiento del resto de documentos...");
          
          // Filtrar documentos que no son carnets de identidad o si son más de 2
          const otrosDocumentos = docsWithUri.filter(doc => 
            doc.tipo !== "Fotocopia Carnet de Identidad" || carnetDocumentos.length !== 2
          );
          
          if (otrosDocumentos.length > 0) {
            console.log(`Se procesarán ${otrosDocumentos.length} documentos adicionales`);
            
            // Agrupar documentos por tipo (excluyendo los 2 carnets que ya se procesaron)
            const documentosPorTipo = {};
            otrosDocumentos.forEach(doc => {
              if (!documentosPorTipo[doc.tipo]) {
                documentosPorTipo[doc.tipo] = [];
              }
              documentosPorTipo[doc.tipo].push(doc);
            });
            
            console.log("Documentos agrupados por tipo (excluyendo los 2 carnets):", documentosPorTipo);
            
            // Establecer timeout para detectar problemas de conexión
            const TIMEOUT_DURATION = 30000; // 30 segundos
            
            // Crear una promesa con timeout para detectar problemas de conexión
            const fetchWithTimeout = async (url, options, timeout) => {
              const controller = new AbortController();
              const id = setTimeout(() => controller.abort(), timeout);
              
              try {
                const response = await fetch(url, {
                  ...options,
                  signal: controller.signal
                });
                clearTimeout(id);
                return response;
              } catch (error) {
                clearTimeout(id);
                throw error;
              }
            };
            
            // Procesar cada tipo de documento
            for (const [tipo, docs] of Object.entries(documentosPorTipo)) {
              console.log(`=== PROCESANDO GRUPO DE DOCUMENTOS: ${tipo} ===`);
              console.log(`Número de documentos en este grupo: ${docs.length}`);
              
              try {
                // Extraer todos los URIs de gsutil para este tipo
                const urisDeGrupo = docs.map(doc => doc.gsutilUri).filter(Boolean);
                console.log(`URIs en este grupo: ${urisDeGrupo.length}`);
                
                if (urisDeGrupo.length === 0) {
                  console.error(`No se encontraron URIs válidos para el tipo ${tipo}`);
                  continue;
                }
                
                // Verificar explícitamente los URIs
                const urisInvalidos = urisDeGrupo.filter(uri => 
                  !uri || typeof uri !== 'string' || !uri.startsWith('gs://')
                );
                
                if (urisInvalidos.length > 0) {
                  console.error(`Se encontraron ${urisInvalidos.length} URIs inválidos en el grupo ${tipo}`);
                  results.push({
                    success: false,
                    documentType: tipo,
                    count: docs.length,
                    error: `${urisInvalidos.length} URI(s) de gsutil con formato incorrecto`
                  });
                  continue;
                }
                
                console.log(`Enviando ${urisDeGrupo.length} URI(s) para el tipo: "${tipo}"`);
                console.log(`Usando RUT formateado: "${formattedRut}" para todos los documentos`);
                
                // Preparar datos de la solicitud - Incluir RUT, MÚLTIPLES URIs y tipo de documento
                const requestData = {
                  personalIdentifier: formattedRut,
                  gsutilUris: urisDeGrupo, // Enviar array de URIs
                  documentType: tipo
                };
                
                // Mostrar exactamente lo que estamos enviando
                console.log("Datos de la solicitud (objeto):", requestData);
                console.log(`Tipo de documento: "${tipo}"`);
                console.log("Datos de la solicitud (JSON):", JSON.stringify(requestData));
                
                // Realizar la solicitud con timeout
                console.log(`Enviando solicitud a: ${updateBizneoUrl}`);
                const response = await fetchWithTimeout(
                  updateBizneoUrl,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestData)
                  },
                  TIMEOUT_DURATION
                );
                
                console.log(`Respuesta recibida para grupo ${tipo}:`);
                console.log(`  - Status: ${response.status}`);
                console.log(`  - OK: ${response.ok}`);
                console.log(`  - StatusText: ${response.statusText}`);
                
                // Capturar la respuesta completa
                const responseText = await response.text();
                console.log(`  - Texto completo: "${responseText}"`);
                
                // Si la respuesta es exitosa, procesar el resultado
                if (response.ok) {
                  let data;
                  try {
                    // Intentar parsear como JSON si es posible
                    data = JSON.parse(responseText);
                    console.log(`  - Datos JSON:`, data);
                  } catch (e) {
                    console.log(`  - No es JSON válido, usando como texto`);
                    data = { message: responseText };
                  }
                  
                  results.push({
                    success: true,
                    documentType: tipo,
                    count: docs.length,
                    data: data
                  });
                } else {
                  // Si hay un error, capturarlo
                  console.error(`Error al enviar documentos de tipo ${tipo} - Status ${response.status}:`, responseText);
                  
                  let errorMsg;
                  try {
                    // Intentar parsear el error como JSON
                    const errorJson = JSON.parse(responseText);
                    errorMsg = errorJson.error || errorJson.message || `Error ${response.status}`;
                    console.log(`  - Error JSON:`, errorJson);
                    
                    // Detectar errores específicos como RUT no encontrado en Bizneo
                    if (responseText.includes("candidate not found") || 
                        responseText.includes("No se encuentra candidato") ||
                        responseText.includes("no se encuentra un candidato") ||
                        responseText.toLowerCase().includes("not found") ||
                        responseText.toLowerCase().includes("no encontrado")) {
                      errorMsg = `RUT no encontrado en Bizneo: El candidato con RUT ${formattedRut} no existe en el sistema.`;
                    }
                    // Otros tipos de error...
                  } catch (e) {
                    errorMsg = `Error ${response.status}: ${response.statusText || responseText}`;
                    // Detectar tipos de error en texto plano...
                  }
                  
                  results.push({
                    success: false,
                    documentType: tipo,
                    count: docs.length,
                    error: errorMsg
                  });
                }
              } catch (error) {
                // Capturar errores de red u otros
                console.error(`Error al procesar documentos de tipo ${tipo}:`, error);
                
                // Personalizar mensaje según el tipo de error
                let errorMessage = `${error.name}: ${error.message}`;
                // Personalizar mensajes para tipos de error específicos...
                
                results.push({
                  success: false,
                  documentType: tipo,
                  count: docs.length,
                  error: errorMessage
                });
              }
            }
          } else {
            console.log("No hay documentos adicionales para procesar");
          }
          
          // Actualizar estado con todos los resultados
          setUploadResults(results);
          
          // Mostrar notificaciones basadas en todos los resultados
          const totalDocs = docsWithUri.length;
          const successCount = results.filter(r => r.success).reduce((total, r) => total + r.count, 0);
          
          if (successCount === totalDocs) {
            showNotification(`Todos los documentos (${successCount}) fueron enviados correctamente.`, "success");
          } else {
            // Construir un mensaje detallando qué documentos fallaron y por qué
            const failedResults = results.filter(r => !r.success);
            if (failedResults.length > 0) {
              const errorDetails = failedResults.map(r => `${r.documentType} (${r.count}): ${r.error}`).join('; ');
              showNotification(`Error al enviar algunos documentos: ${errorDetails}`, "error");
            } else {
              showNotification(`Se enviaron ${successCount} de ${totalDocs} documentos. Revise la consola para detalles.`, "warning");
            }
          }
          
          // Quitar el indicador de carga
          setUploading(false);
          return;
        } catch (error) {
          console.error("Error al procesar los 2 documentos de Carnet:", error);
          showNotification(`Error al procesar los documentos de Carnet: ${error.message}`, "error");
          setUploading(false);
          return;
        }
      }
      
      // Si no hay exactamente 2 documentos de carnet, o si hubo un error en el procesamiento especial,
      // continuar con el flujo normal
      
      // MODIFICACIÓN: Agrupar documentos por tipo
      const documentosPorTipo = {};
      docsWithUri.forEach(doc => {
        if (!documentosPorTipo[doc.tipo]) {
          documentosPorTipo[doc.tipo] = [];
        }
        documentosPorTipo[doc.tipo].push(doc);
      });
      
      console.log("Documentos agrupados por tipo:", documentosPorTipo);
      
      // Crear un array para almacenar los resultados
      const results = [];
      
      // Establecer timeout para detectar problemas de conexión
      const TIMEOUT_DURATION = 30000; // 30 segundos
      
      // MODIFICACIÓN: Enviar documentos por tipo (no individualmente)
      for (const [tipo, docs] of Object.entries(documentosPorTipo)) {
        // Saltarse los documentos de Carnet de Identidad si hay exactamente 2 (ya fueron procesados)
        if (tipo === "Fotocopia Carnet de Identidad" && docs.length === 2) {
          console.log("Saltando procesamiento normal para los 2 carnets (ya procesados)");
          continue;
        }
        
        console.log(`=== PROCESANDO GRUPO DE DOCUMENTOS: ${tipo} ===`);
        console.log(`Número de documentos en este grupo: ${docs.length}`);
        
        try {
          // Extraer todos los URIs de gsutil para este tipo
          const urisDeGrupo = docs.map(doc => doc.gsutilUri).filter(Boolean);
          console.log(`URIs en este grupo: ${urisDeGrupo.length}`);
          
          if (urisDeGrupo.length === 0) {
            console.error(`No se encontraron URIs válidos para el tipo ${tipo}`);
            continue;
          }
          
          // Verificar explícitamente los URIs
          const urisInvalidos = urisDeGrupo.filter(uri => 
            !uri || typeof uri !== 'string' || !uri.startsWith('gs://')
          );
          
          if (urisInvalidos.length > 0) {
            console.error(`Se encontraron ${urisInvalidos.length} URIs inválidos en el grupo ${tipo}`);
            results.push({
              success: false,
              documentType: tipo,
              count: docs.length,
              error: `${urisInvalidos.length} URI(s) de gsutil con formato incorrecto`
            });
            continue;
          }
          
          console.log(`Enviando ${urisDeGrupo.length} URI(s) para el tipo: "${tipo}"`);
          console.log(`Usando RUT formateado: "${formattedRut}" para todos los documentos`);
          
          // Preparar datos de la solicitud - Incluir RUT, MÚLTIPLES URIs y tipo de documento
          const requestData = {
            personalIdentifier: formattedRut,
            gsutilUris: urisDeGrupo, // Enviar array de URIs
            documentType: tipo
          };
          
          // Mostrar exactamente lo que estamos enviando
          console.log("Datos de la solicitud (objeto):", requestData);
          console.log(`Tipo de documento: "${tipo}"`);
          console.log("Datos de la solicitud (JSON):", JSON.stringify(requestData));
          
          // Crear una promesa con timeout para detectar problemas de conexión
          const fetchWithTimeout = async (url, options, timeout) => {
            const controller = new AbortController();
            const id = setTimeout(() => controller.abort(), timeout);
            
            try {
              const response = await fetch(url, {
                ...options,
                signal: controller.signal
              });
              clearTimeout(id);
              return response;
            } catch (error) {
              clearTimeout(id);
              throw error;
            }
          };
          
          // Determinar la URL a usar según el tipo y cantidad de documentos
          let targetUrl = updateBizneoUrl;
          if (tipo === "Fotocopia Carnet de Identidad" && docs.length === 2) {
            targetUrl = carnetDobleFunctionUrl;
            console.log(`Usando endpoint especial para 2 carnets de identidad: ${targetUrl}`);
          }
          
          // Realizar la solicitud con timeout
          console.log(`Enviando solicitud a: ${targetUrl}`);
          const response = await fetchWithTimeout(
            targetUrl,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(requestData)
            },
            TIMEOUT_DURATION
          );
          
          console.log(`Respuesta recibida para grupo ${tipo}:`);
          console.log(`  - Status: ${response.status}`);
          console.log(`  - OK: ${response.ok}`);
          console.log(`  - StatusText: ${response.statusText}`);
          
          // Capturar la respuesta completa
          const responseText = await response.text();
          console.log(`  - Texto completo: "${responseText}"`);
          
          // Si la respuesta es exitosa, procesar el resultado
          if (response.ok) {
            let data;
            try {
              // Intentar parsear como JSON si es posible
              data = JSON.parse(responseText);
              console.log(`  - Datos JSON:`, data);
            } catch (e) {
              console.log(`  - No es JSON válido, usando como texto`);
              data = { message: responseText };
            }
            
            results.push({
              success: true,
              documentType: tipo,
              count: docs.length,
              data: data
            });
          } else {
            // Si hay un error, capturarlo
            console.error(`Error al enviar documentos de tipo ${tipo} - Status ${response.status}:`, responseText);
            
            let errorMsg;
            try {
              // Intentar parsear el error como JSON
              const errorJson = JSON.parse(responseText);
              errorMsg = errorJson.error || errorJson.message || `Error ${response.status}`;
              console.log(`  - Error JSON:`, errorJson);
              
              // Detectar errores específicos como RUT no encontrado en Bizneo
              if (responseText.includes("candidate not found") || 
                  responseText.includes("No se encuentra candidato") ||
                  responseText.includes("no se encuentra un candidato") ||
                  responseText.toLowerCase().includes("not found") ||
                  responseText.toLowerCase().includes("no encontrado")) {
                errorMsg = `RUT no encontrado en Bizneo: El candidato con RUT ${formattedRut} no existe en el sistema.`;
              }
              // Detectar error de acceso denegado o permisos insuficientes
              else if (responseText.toLowerCase().includes("permission") || 
                       responseText.toLowerCase().includes("unauthorized") || 
                       responseText.toLowerCase().includes("forbidden") ||
                       responseText.toLowerCase().includes("acceso denegado") ||
                       response.status === 401 ||
                       response.status === 403) {
                errorMsg = "Error de permisos: No tiene autorización para subir documentos a este candidato.";
              }
              // Detectar error de límites o cuotas excedidas
              else if (responseText.toLowerCase().includes("quota") || 
                       responseText.toLowerCase().includes("limit") ||
                       responseText.toLowerCase().includes("límite") ||
                       responseText.toLowerCase().includes("cuota") ||
                       response.status === 429) {
                errorMsg = "Error de límites: Se ha excedido la cuota de subida de archivos. Intente más tarde.";
              }
              // Detectar error de campos incompletos en perfil de Bizneo
              else if (responseText.toLowerCase().includes("field") || 
                       responseText.toLowerCase().includes("missing") ||
                       responseText.toLowerCase().includes("required") ||
                       responseText.toLowerCase().includes("campo") ||
                       responseText.toLowerCase().includes("requerido") ||
                       responseText.toLowerCase().includes("obligatorio")) {
                errorMsg = "Error de datos: El perfil del candidato en Bizneo no tiene todos los campos requeridos.";
              }
            } catch (e) {
              errorMsg = `Error ${response.status}: ${response.statusText || responseText}`;
              
              // Intentar detectar errores específicos en texto plano
              if (responseText.includes("candidate not found") || 
                  responseText.includes("No se encuentra candidato") ||
                  responseText.includes("no se encuentra un candidato") ||
                  responseText.toLowerCase().includes("not found") ||
                  responseText.toLowerCase().includes("no encontrado")) {
                errorMsg = `RUT no encontrado en Bizneo: El candidato con RUT ${formattedRut} no existe en el sistema.`;
              }
              // Otros casos de error en texto plano
              else if (response.status === 401 || response.status === 403) {
                errorMsg = "Error de permisos: No tiene autorización para subir documentos a este candidato.";
              }
              else if (response.status === 429) {
                errorMsg = "Error de límites: Se ha excedido la cuota de subida de archivos. Intente más tarde.";
              }
              else if (response.status === 413) {
                errorMsg = "Error de tamaño: El archivo es demasiado grande para ser procesado por Bizneo.";
              }
              else if (response.status >= 500) {
                errorMsg = "Error del servidor: Bizneo está experimentando problemas. Intente más tarde.";
              }
            }
            
            results.push({
              success: false,
              documentType: tipo,
              count: docs.length,
              error: errorMsg
            });
          }
        } catch (error) {
          // Capturar errores de red u otros
          console.error(`Error al procesar documentos de tipo ${tipo}:`, error);
          console.error(`  - Tipo de error: ${error.name}`);
          console.error(`  - Mensaje: ${error.message}`);
          console.error(`  - Stack: ${error.stack}`);
          
          // Personalizar mensaje según el tipo de error
          let errorMessage = `${error.name}: ${error.message}`;
          
          if (error.name === 'AbortError') {
            errorMessage = "Tiempo de espera excedido: El servidor tardó demasiado en responder. Verifique su conexión a internet.";
          } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
            errorMessage = "Error de conexión: No se pudo conectar con el servidor. Verifique su conexión a internet.";
          } else if (error.name === 'SyntaxError') {
            errorMessage = "Error de formato: La respuesta del servidor no tiene el formato esperado.";
          }
          
          results.push({
            success: false,
            documentType: tipo,
            count: docs.length,
            error: errorMessage
          });
        }
      }
      
      console.log("=== RESULTADOS FINALES ===");
      console.log("Todos los resultados:", results);
      
      // Actualizar estado con los resultados
      setUploadResults(results);
      
      // Verificar si hubo errores de "RUT no encontrado"
      const rutNotFoundError = results.find(r => !r.success && r.error && r.error.toLowerCase().includes("rut no encontrado"));
      
      if (rutNotFoundError) {
        showNotification(rutNotFoundError.error, "error");
      } else {
        // Verificar si hubo errores de conexión
        const connectionError = results.find(r => !r.success && r.error && (
          r.error.includes("Tiempo de espera excedido") || 
          r.error.includes("Error de conexión")
        ));
        
        if (connectionError) {
          showNotification(connectionError.error, "error");
        } else {
          // Verificar si hubo errores de permisos
          const permissionError = results.find(r => !r.success && r.error && r.error.includes("Error de permisos"));
          
          if (permissionError) {
            showNotification(permissionError.error, "error");
          } else {
            // Verificar si hubo errores de límites
            const limitError = results.find(r => !r.success && r.error && r.error.includes("Error de límites"));
            
            if (limitError) {
              showNotification(limitError.error, "error");
            } else {
              // Mostrar mensaje según los resultados
              const totalDocs = Object.values(documentosPorTipo).reduce((total, docs) => total + docs.length, 0);
              const successCount = results.filter(r => r.success).reduce((total, r) => total + r.count, 0);
              
              if (successCount === totalDocs) {
                showNotification(`Todos los documentos (${successCount}) fueron enviados correctamente.`, "success");
              } else {
                // Construir un mensaje detallando qué documentos fallaron y por qué
                const failedResults = results.filter(r => !r.success);
                if (failedResults.length > 0) {
                  const errorDetails = failedResults.map(r => `${r.documentType} (${r.count}): ${r.error}`).join('; ');
                  showNotification(`Error al enviar algunos documentos: ${errorDetails}`, "error");
                } else {
                  showNotification(`Se enviaron ${successCount} de ${totalDocs} documentos. Revise la consola para detalles.`, "warning");
                }
              }
            }
          }
        }
      }
      
    } catch (error) {
      console.error("=== ERROR GENERAL ===");
      console.error("Tipo:", error.name);
      console.error("Mensaje:", error.message);
      console.error("Stack:", error.stack);
      
      // Personalizar mensaje según el tipo de error
      let errorMessage = `Ocurrió un error: ${error.message}`;
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        errorMessage = "Error de conexión: No se pudo conectar con el servidor. Verifique su conexión a internet.";
      } else if (error.name === 'AbortError') {
        errorMessage = "Tiempo de espera excedido: El servidor tardó demasiado en responder. Verifique su conexión a internet.";
      } else if (error.name === 'QuotaExceededError') {
        errorMessage = "Error de almacenamiento: Se ha excedido la cuota de almacenamiento. Libere espacio e intente nuevamente.";
      }
      
      showNotification(errorMessage, "error");
    } finally {
      console.log("=== FIN CONFIRMACIÓN ===");
      // Independientemente del resultado, quitar el indicador de carga
      setUploading(false);
    }
  };

  // Función para mostrar el estado de carga
  const getStatusIcon = (status) => {
    switch(status) {
      case 'uploading':
        return null; // Removing the hourglass icon
      case 'success':
        return null; // Removing the checkmark icon
      case 'error':
        return '❌';
      default:
        return null;
    }
  };

  // Add dot loading animation styles
  React.useEffect(() => {
    // Add CSS for dot loading animation and button styles
    const style = document.createElement('style');
    style.innerHTML = `
      .dot-loading {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      
      .dot {
        width: 8px;
        height: 8px;
        margin: 0 4px;
        border-radius: 50%;
        background-color: #4a4a4a;
        animation: dotPulse 1.4s infinite ease-in-out;
      }
      
      .dot:nth-child(1) {
        animation-delay: 0s;
      }
      
      .dot:nth-child(2) {
        animation-delay: 0.2s;
      }
      
      .dot:nth-child(3) {
        animation-delay: 0.4s;
      }
      
      @keyframes dotPulse {
        0%, 100% {
          transform: scale(0.6);
          opacity: 0.6;
        }
        50% {
          transform: scale(1);
          opacity: 1;
        }
      }
      
      /* Estilos para el botón deshabilitado */
      .button-submit:disabled {
        background-color: #cccccc;
        color: #888888;
        cursor: not-allowed;
        opacity: 0.7;
        border: 1px solid #aaaaaa;
      }
      
      .button-submit:disabled:hover {
        background-color: #cccccc;
        box-shadow: none;
      }
      
      /* Estilos para el checkbox de confirmación consciente */
      .confirmacion-consciente-container {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        margin: 20px auto;
        padding: 15px 20px;
        background-color: #fff8e1;
        border-left: 4px solid #ffc107;
        border-radius: 8px;
        width: 100%;
        max-width: 750px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.08);
        transition: all 0.3s ease;
      }
      
      .confirmacion-consciente-container:hover {
        box-shadow: 0 4px 8px rgba(0,0,0,0.12);
        transform: translateY(-1px);
      }
      
      .confirmacion-consciente-label {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-weight: 500;
        color: #775700;
        margin: 0;
        font-size: 14px;
        line-height: 1.4;
        width: 100%;
      }
      
      .confirmacion-consciente-checkbox {
        margin-right: 12px;
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: #fd8c00;
      }
      
      .warning-icon {
        margin-right: 15px;
        color: #fd8c00;
        font-size: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .warning-icon-svg {
        width: 24px;
        height: 24px;
      }
      
      .documento-item {
        width: 750px;
        margin: 15px auto;
        display: flex;
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        overflow: hidden;
        padding: 10px;
      }
      
      .documento-info {
        flex: 1;
        padding: 10px 15px;
        display: flex;
        flex-direction: column;
      }
      
      .documento-nombre {
        font-weight: 600;
        margin-bottom: 10px;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }
      
      .documento-actions {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        margin-top: 8px;
        width: 100%;
      }
      
      .documento-select {
        width: 36%;
        margin-right: 15px;
        padding: 8px;
        border-radius: 4px;
        border: 1px solid #ddd;
      }
      
      .button-table {
        padding: 8px 15px;
        margin-right: 10px;
      }
      
      .loading-container {
        display: flex;
        justify-content: center;
        padding: 15px 0;
      }
      
      .rut-container {
        position: relative;
        margin-bottom: 35px;
      }
      
      .rut-error {
        color: #e53935;
        font-size: 14px;
        margin-top: 5px;
        font-weight: 500;
        position: absolute;
        width: 100%;
        left: 0;
        top: 100%;
      }
      
      .rut-warning {
        color: #ff9800;
        font-size: 14px;
        margin-top: 5px;
        font-weight: 500;
        position: absolute;
        width: 100%;
        left: 0;
        top: 100%;
      }
      
      .input-error {
        border-color: #e53935 !important;
        background-color: #ffebee !important;
      }
      
      .gsutil-uri-container {
        margin-top: 8px;
        padding: 8px 10px;
        background-color: #f5f5f5;
        border-radius: 4px;
        overflow: hidden;
        text-overflow: ellipsis;
        border-left: 3px solid #2196f3;
      }
      
      .gsutil-uri {
        color: #333;
        font-size: 13px;
        font-family: monospace;
        word-break: break-all;
      }
      
      .gsutil-missing {
        background-color: #fff3cd;
        border-left: 3px solid #ffc107;
        color: #856404;
      }
      
      .file-reference {
        margin-top: 4px;
        font-style: italic;
        font-size: 12px;
      }
      
      /* Estilos para las notificaciones de Bootstrap */
      .notification-bootstrap {
        position: fixed;
        top: 20px;
        right: 20px;
        width: auto;
        max-width: 400px;
        z-index: 9999;
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
      }
      
      /* Estilos para los diferentes tipos de alertas */
      .alert {
        padding: 12px 20px;
        margin-bottom: 20px;
        border: 1px solid transparent;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      
      .alert-success {
        color: #0f5132;
        background-color: #d1e7dd;
        border-color: #badbcc;
      }
      
      .alert-warning {
        color: #664d03;
        background-color: #fff3cd;
        border-color: #ffecb5;
      }
      
      .alert-error,
      .alert-danger {
        color: #842029;
        background-color: #f8d7da;
        border-color: #f5c2c7;
      }
      
      .btn-close {
        background: transparent;
        border: 0;
        font-size: 1.25rem;
        font-weight: 700;
        opacity: 0.5;
        padding: 0 0 0 15px;
        cursor: pointer;
      }
      
      .btn-close:hover {
        opacity: 0.75;
      }
      
      .fade {
        transition: opacity 0.15s linear;
      }
      
      .fade.show {
        opacity: 1;
      }
      
      .button-delete {
        margin-left: 5px;
        background-color: transparent;
        color: #f44336;
        border: none;
        padding: 6px 10px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }
      
      .button-delete:hover {
        background-color: rgba(244, 67, 54, 0.1);
      }
      
      .trash-icon {
        width: 20px;
        height: 20px;
        fill: #f44336;
      }
      
      .button-view {
        background-color: transparent;
        color: #2196f3;
        border: none;
        padding: 6px 10px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        margin-right: 10px;
      }
      
      .button-view:hover {
        background-color: rgba(33, 150, 243, 0.1);
      }
      
      .eye-icon {
        width: 20px;
        height: 20px;
        fill: #2196f3;
      }
      
      /* Eliminar el estilo para destacar documentos con RUT diferente */
      .documento-rut-diferente {
        display: none; /* Ya no se usa este estilo */
      }
      
      /* Estilos mejorados para la etiqueta de RUT */
      .rut-badge {
        display: flex;
        align-items: center;
        padding: 5px 8px;
        background-color: #e8f5e9;
        color: #2e7d32;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        border: 1px solid #c8e6c9;
        transition: all 0.2s ease;
        margin-right: 15px;
        white-space: nowrap;
        min-width: 130px;
      }
      
      .rut-badge:hover {
        box-shadow: 0 2px 5px rgba(0,0,0,0.15);
        transform: translateY(-1px);
      }
      
      .rut-diferente {
        background-color: #ffecb3;
        color: #e65100;
        border-color: #ffcc80;
        animation: pulseWarning 2s infinite;
        font-weight: 600;
      }
      
      .rut-sin-rut {
        background-color: #ffcdd2;
        color: #c62828;
        border-color: #ef9a9a;
      }
      
      @keyframes pulseWarning {
        0% {
          box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.4);
        }
        70% {
          box-shadow: 0 0 0 6px rgba(255, 152, 0, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
        }
      }
      
      .rut-icon {
        margin-right: 6px;
        font-size: 14px;
      }
      
      .rut-text {
        font-family: 'Consolas', monospace;
        letter-spacing: 0.5px;
      }
      
      /* Estilo para documentos sin-rut - Ya no se necesita */
      .documento-sin-rut {
        display: none;
      }
      
      /* Estilos para etiquetas de tipo de archivo */
      .pdf-label, .word-label, .img-label, .other-label {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 60px;
        height: 28px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: bold;
        color: white;
        margin-top: 10px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.12);
      }
      
      .pdf-label {
        background-color: #f44336;
      }
      
      .word-label {
        background-color: #2196f3;
      }
      
      .img-label {
        background-color: #ffc107;
        color: #333;
      }
      
      .other-label {
        background-color: #9e9e9e;
      }
      
      /* Estilos para visualización de documentos Word */
      .word-viewer-container, .generic-viewer-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        padding: 20px;
        background-color: #f9f9f9;
      }
      
      .word-preview-message, .generic-preview-message {
        text-align: center;
        background-color: white;
        border-radius: 8px;
        padding: 30px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        max-width: 400px;
      }
      
      .word-icon {
        margin-bottom: 20px;
      }
      
      .download-button {
        display: inline-block;
        background-color: #2196f3;
        color: white;
        padding: 10px 20px;
        border-radius: 4px;
        margin-top: 20px;
        text-decoration: none;
        font-weight: 500;
        transition: background-color 0.2s;
      }
      
      .download-button:hover {
        background-color: #1976d2;
      }
      
      .image-viewer-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        padding: 20px;
        background-color: #f9f9f9;
      }
      
      .image-preview {
        max-width: 100%;
        max-height: 80vh;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      }
      
      /* Estilos para el botón de limpiar todo */
      .button-clear-all {
        background-color: #f44336;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 30px;
        font-weight: bold;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.3s, box-shadow 0.3s, background-color 0.3s;
        width: 180px;
        height: 45px;
        box-shadow: 0 2px 4px rgba(244, 67, 54, 0.3);
        letter-spacing: 0.5px;
        font-family: inherit;
        font-size: 16px;
      }
      
      .button-clear-all:hover {
        background-color: #e53935;
        box-shadow: 0 6px 12px rgba(244, 67, 54, 0.4);
        transform: scale(1.05);
      }
      
      .button-clear-all:active {
        background-color: #e53935;
        box-shadow: 0 2px 4px rgba(244, 67, 54, 0.3);
        transform: scale(0.97);
      }
      
      .clear-icon {
        margin-right: 8px;
        fill: white;
        width: 18px;
        height: 18px;
      }
      
      /* Contenedor para alinear los botones en extremos opuestos */
      .buttons-container {
        display: flex;
        justify-content: center;
        gap: 40px; /* Espacio entre botones */
        align-items: center;
        width: 100%;
        max-width: 750px;
        margin: 25px auto;
      }
      
      /* Ajustar el botón submit para que coincida en altura con el botón limpiar */
      .button-submit {
        height: 45px;
        width: 180px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Loading animation component
  const DotLoading = () => (
    <div className="dot-loading">
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
    </div>
  );

  // Componente Modal simple
  const Modal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>×</button>
          {children}
        </div>
      </div>
    );
  };

  // Componente para visualizar documentos
  const DocumentViewer = ({ archivo, fileType, fileName }) => {
    // Para archivos PDF, mostrar en iframe
    if (fileType === 'pdf') {
      return (
        <iframe
          src={archivo}
          className="pdf-viewer"
          title="PDF Viewer"
        />
      );
    }
    
    // Para archivos de Word, mostrar mensaje y botón de descarga
    else if (fileType === 'word') {
      return (
        <div className="word-viewer-container">
          <div className="word-preview-message">
            <div className="word-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 8.5V20.5H4V3.5H14L20 8.5Z" fill="#2196F3" />
                <path d="M14 3.5V8.5H20L14 3.5Z" fill="#0D47A1" />
                <path d="M6.977 13.036L8.399 18.5H9.618L11.643 11.477H10.424L9.154 16.757L7.812 11.477H6.202L4.838 16.757L3.59 11.477H2.357L4.358 18.5H5.577L6.977 13.036Z" fill="white" />
              </svg>
            </div>
            <h3>Documento Word</h3>
            <p>Los documentos de Word no se pueden previsualizar directamente.</p>
            <a 
              href={archivo} 
              download={fileName} 
              className="download-button"
            >
              Descargar documento
            </a>
          </div>
        </div>
      );
    }
    
    // Para imágenes, mostrar la imagen directamente
    else if (fileType === 'image') {
      return (
        <div className="image-viewer-container">
          <img 
            src={archivo} 
            alt={fileName} 
            className="image-preview" 
          />
        </div>
      );
    }
    
    // Para otros tipos de archivo, mostrar mensaje genérico y botón de descarga
    else {
      return (
        <div className="generic-viewer-container">
          <div className="generic-preview-message">
            <h3>No se puede previsualizar</h3>
            <p>Este tipo de archivo no se puede previsualizar directamente.</p>
            <a 
              href={archivo} 
              download={fileName} 
              className="download-button"
            >
              Descargar archivo
            </a>
          </div>
        </div>
      );
    }
  };

  // Función para mostrar notificaciones
  const showNotification = (message, type) => {
    setNotification({
      show: true,
      message,
      type
    });
    
    // Ocultar la notificación después de 5 segundos
    setTimeout(() => {
      setNotification(prev => ({...prev, show: false}));
    }, 5000);
  };

  // Función para validar el tamaño máximo de archivo (20MB)
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB en bytes

  const validateFileSize = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `El archivo excede el tamaño máximo permitido (20MB). Tamaño actual: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
      };
    }
    return { valid: true };
  };

  // Función para validar tipo de archivo
  const validateFileType = (file) => {
    const allowedTypes = [
      'application/pdf', 
      'image/jpeg', 
      'image/jpg', 
      'image/png',
      'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Tipo de archivo no permitido: ${file.type}. Solo se permiten PDF, Word, JPG y PNG.`
      };
    }
    
    // Validación adicional para detectar posibles archivos corruptos o malformados
    if (file.size === 0) {
      return {
        valid: false,
        error: 'El archivo está vacío y no puede ser procesado.'
      };
    }
    
    return { valid: true };
  };

  // Función para determinar el tipo de archivo
  const getFileTypeInfo = (file) => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type;
    
    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
      return { type: 'pdf', label: 'PDF', className: 'pdf-label' };
    } else if (
      fileType === 'application/msword' || 
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileName.endsWith('.doc') || 
      fileName.endsWith('.docx')
    ) {
      return { type: 'word', label: 'WORD', className: 'word-label' };
    } else if (
      fileType.startsWith('image/') || 
      fileName.endsWith('.jpg') || 
      fileName.endsWith('.jpeg') || 
      fileName.endsWith('.png')
    ) {
      return { type: 'image', label: 'IMG', className: 'img-label' };
    }
    
    return { type: 'other', label: 'FILE', className: 'other-label' };
  };

  // Función para verificar si hay documentos con RUT diferente o sin RUT
  const hayDocumentosConRutDiferente = () => {
    if (!rut) return false;
    
    return documentos.some(doc => 
      doc.status === 'success' && 
      doc.analysis && 
      ((doc.analysis.RUT && doc.analysis.RUT !== rut) || 
       doc.analysis.RUT === 'sin-rut')
    );
  };

  return (
    <div>
      <TopBarHeader />
      <h1 className="h1-titulo-2">Carga de documentos a Bizneo</h1>
      <p className="description">
        Identifica y procesa documentos de trabajadores utilizando esta herramienta.
      </p>
      <p className="description">
      El perfil debe estar creado en Bizneo.
      </p>

      {/* Notificación estilo Bootstrap */}
      {notification.show && (
        <div className={`alert alert-${notification.type} alert-dismissible fade show notification-bootstrap`} role="alert">
          {notification.message}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setNotification({...notification, show: false})}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
      )}

      <div className="upload-container">
        {/* Uploader similar al de asistencia-libro */}
        <div className="upload-box" 
             onClick={handleButtonClick}
             onDragEnter={handleDrag}
             onDragOver={handleDrag}
             onDragLeave={handleDrag}
             onDrop={handleDrop}
        >
          <div className={`upload-area ${dragActive ? "drag-active" : ""}`}>
            <img src={icon} alt="Upload icon" className="upload-icon" />
            <p>Arrastra o sube tus archivos. <span className="browse">Aquí</span></p>
            <p className="supports">Formatos permitidos: .pdf, .doc, .docx, .jpg, .jpeg, .png</p>
            <input 
              ref={inputRef}
              type="file" 
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              multiple
              onChange={handleInputChange}
              className="file-input-hidden" 
            />
          </div>
        </div>
        
        {/* Mostrar documentos y campo RUT sólo si hay documentos */}
        {documentos.length > 0 && (
          <>
            {/* Campo RUT (ahora debajo de la zona de arrastrar archivos) */}
            <div className="rut-container">
              <label htmlFor="rut" className="rut-label">RUT:</label>
              <input
                type="text"
                id="rut"
                className={`input-moderno ${rutError ? 'input-error' : ''}`}
                value={rut}
                onChange={handleRutChange}
                placeholder="12.345.678-9"
                maxLength="12"
              />
              {rutError && <div className="rut-error">RUT inválido</div>}
              {!rut && <div className="rut-warning">Ingrese un RUT para poder confirmar</div>}
            </div>
            
            {/* Lista de documentos */}
            <div className="documentos-list">
              {documentos.map((doc) => {
                // Verificar si el documento tiene un RUT diferente
                const tieneDiferenteRut = doc.status === 'success' && doc.analysis && doc.analysis.RUT && rut && doc.analysis.RUT !== rut;
                // Verificar si el documento tiene un RUT
                const tieneRut = doc.status === 'success' && doc.analysis && doc.analysis.RUT;
                // Verificar si el documento tiene "sin-rut"
                const esSinRut = doc.status === 'success' && doc.analysis && doc.analysis.RUT === 'sin-rut';
                
                return (
                <div 
                  key={doc.id} 
                  className="documento-item"
                >
                  <div className="documento-icon-container">
                    {doc.archivo && (
                      <div className={getFileTypeInfo(doc.archivo).className}>
                        {getFileTypeInfo(doc.archivo).label}
                      </div>
                    )}
                    <div className="image-placeholder">
                      <div className="image-icon"></div>
                    </div>
                  </div>
                  <div className="documento-info">
                    <span className="status-icon">{getStatusIcon(doc.status)}</span>
                    
                    <div className="documento-nombre">
                      {doc.nombre}
                    </div>
                    
                    {doc.status === 'uploading' ? (
                      <div className="loading-container">
                        <DotLoading />
                      </div>
                    ) : (
                      <>
                        <div className="documento-actions">
                          <select 
                            className="documento-select"
                            value={doc.tipo}
                            onChange={(e) => handleDocumentTypeChange(doc.id, e.target.value)}
                            disabled={doc.status === 'uploading'}
                          >
                            {TIPOS_DOCUMENTO.map((tipo, index) => (
                              <option key={index} value={tipo}>
                                {tipo}
                              </option>
                            ))}
                          </select>
                          
                          {tieneRut && (
                            <div className={`rut-badge ${tieneDiferenteRut ? 'rut-diferente' : ''} ${(esSinRut || (doc.analysis.RUT && !validateRut(doc.analysis.RUT))) ? 'rut-sin-rut' : ''}`}>
                              <span className="rut-icon">🆔</span>
                              <span className="rut-text">{(doc.analysis.RUT && validateRut(doc.analysis.RUT)) ? doc.analysis.RUT : 'sin-rut'}</span>
                            </div>
                          )}
                          
                          <button 
                            type="button"
                            className="button-table button-view" 
                            onClick={() => openModal(doc)}
                            disabled={doc.status === 'uploading'}
                            title="Ver documento"
                          >
                            <img src={eyeIcon} alt="Ver" className="eye-icon" />
                          </button>
                          <button 
                            type="button"
                            className="button-table button-delete" 
                            onClick={() => handleDeleteDocument(doc.id)}
                            disabled={doc.status === 'uploading'}
                            title="Eliminar documento"
                          >
                            <img src={trashIcon} alt="Eliminar" className="trash-icon" />
                          </button>
                        </div>
                        
                        {/* URI de gsutil no se muestra en la interfaz */}
                      </>
                    )}
                    
                    {doc.status === 'error' && (
                      <div className="error-message">
                        <small>Error: {doc.error}</small>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
            
            {/* Checkbox para confirmación consciente */}
            {hayDocumentosConRutDiferente() && (
              <div className="confirmacion-consciente-container">
                <div className="warning-icon">
                  <svg className="warning-icon-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                  </svg>
                </div>
                <label className="confirmacion-consciente-label">
                  <input
                    type="checkbox"
                    checked={confirmaciónConsciente}
                    onChange={(e) => setConfirmaciónConsciente(e.target.checked)}
                    className="confirmacion-consciente-checkbox"
                  />
                  <span>Confirmo que estoy subiendo conscientemente documentos con RUTs diferentes o sin RUT</span>
                </label>
              </div>
            )}
            
            {/* Contenedor para los botones */}
            <div className="buttons-container">
              {/* Botón para limpiar todos los documentos */}
              <button 
                type="button"
                className="button-clear-all"
                onClick={handleClearAll}
                title="Eliminar todos los documentos"
              >
                <svg className="clear-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
                Limpiar Todo
              </button>
              
              {/* Botón confirmar con estado de carga y validación de RUT */}
              <button 
                type="button" 
                className="button-submit" 
                onClick={handleConfirmar}
                disabled={uploading || !rut || rutError || (hayDocumentosConRutDiferente() && !confirmaciónConsciente)}
                title={!rut ? "Ingrese un RUT válido para continuar" : 
                      rutError ? "El RUT ingresado no es válido" : 
                      (hayDocumentosConRutDiferente() && !confirmaciónConsciente) ? "Confirme que está subiendo conscientemente documentos con RUTs diferentes" :
                      uploading ? "Enviando documentos..." : ""}
              >
                {uploading ? (
                  <>Enviando <DotLoading /></>
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {selectedDocument && (
          <DocumentViewer 
            archivo={selectedDocument.archivoURL} 
            fileType={selectedDocument.fileType}
            fileName={selectedDocument.fileName}
          />
        )}
      </Modal>
    </div>
  );
};

export default DocumentosTrabajadores;